import { usage } from '../lib/cuiUsage';
import { schreibeMitPlatz, raeumeCachesAuf, istSpeicherVoll, SpeicherVollFehler, bilanzText, speicherBilanz } from '../lib/browserSpeicher';
import { zieheGesendetAb } from './guide-absenden';
export type Choice = { eid: string; kuerzel?: string; freitext?: string; notiz?: string };
export type Stage = { id: string; titel: string; entscheidungen: any[]; nachrichten?: any[]; [key: string]: any };
export type VorschlagWahl = { vid: string; kuerzel: string };
export type Job = { id: string; stage: Stage; latest?: Stage; close?: boolean; choices?: Choice[]; /** Antworten auf Vorschlaege (Route `/vorschlaege/antworten`): ein eigener Auftrag mit eigener Kennung. */ vorschlaege?: VorschlagWahl[]; text?: string; raum?: string; attempted: boolean; serverAccepted?: boolean; retryRequested?: boolean;
  /** Der Server hat die Eingabe beim Absenden endgueltig abgewiesen (4xx) und NICHT gespeichert. */
  rejected?: boolean;
  /** Wann die Eingabe getippt wurde — Reihenfolge je Buehne (siehe `wartetAufVorgaenger`). Fehlt bei Altauftraegen. */
  erstelltAm?: number;
  /** Einmal automatisch neu eingereicht (Altlast der Rafael-Unterschrift) — nie ein zweites Mal. */
  neuEingereicht?: boolean;
  /** Der urspruengliche Serverwortlaut, wenn `error` durch einen Text fuer Partner ersetzt wurde. */
  serverFehler?: string; state: 'queued' | 'sending' | 'uncertain' | 'blocked'; error?: string };
type Store = { readonly length: number; key(index: number): string | null; getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void };
/*
 * EIN SENDEAUFTRAG TRAEGT NUR, WAS ER BRAUCHT — NICHT DIE GANZE BUEHNE (30.09.2026).
 * Bis hierher lag in jedem Auftrag eine volle Kopie der Buehne (gemessen 9–64 KB mit Verlauf
 * und Nachrichten), bei `latest` eine zweite. Gelesen werden davon nur Kennung, Titel, Haus
 * und die Fragen, die der Auftrag beantwortet (`ueberholteWahl` vergleicht genau diese).
 */
export function schlankeBuehne(stage: Stage, choices?: Choice[]): Stage {
  const { id, titel, haus, raum, raum_gesetzt, abschluss } = stage;
  const eids = new Set((choices ?? []).map(c => c.eid));
  return { id, titel, ...(haus !== undefined ? { haus } : {}), ...(raum !== undefined ? { raum } : {}), ...(raum_gesetzt !== undefined ? { raum_gesetzt } : {}), ...(abschluss !== undefined ? { abschluss } : {}),
    entscheidungen: (stage.entscheidungen ?? []).filter(e => eids.has(e?.id)).map(e => JSON.parse(JSON.stringify(e))) };
}
export function schlankerAuftrag(job: Job): Job {
  return { ...job, stage: schlankeBuehne(job.stage, job.choices), ...(job.latest ? { latest: schlankeBuehne(job.latest, job.choices) } : {}) };
}
/** Fuer die Sofort-Aufraeumung: ein gespeicherter Auftrag (oder Archiveintrag) in schlanker Form. */
export function verkleinereGespeichertenAuftrag(wert: string): string {
  const j = JSON.parse(wert);
  if (!j || typeof j !== 'object' || !j.stage || typeof j.stage.id !== 'string') throw Error('kein Sendeauftrag');
  return JSON.stringify(schlankerAuftrag(j));
}
const stable = (v: any): string => v && typeof v === 'object' ? JSON.stringify(Object.keys(v).sort().map(k => [k, stable(v[k])])) : JSON.stringify(v);
function question(e: any) { const { antwort, zustellung, verarbeitet, ...rest } = e; return stable(rest); }
/*
 * WER HAT GEANTWORTET — Rafael ODER die angemeldete Partnerin (Befund 25.09.2026, David/Kurt).
 * Im Haus Partner schreibt das Partner-Tor `von` auf `partner:<id>` um; ein Abgleich nur gegen
 * 'rafael' haette jede angekommene Partner-Antwort nie als bestaetigt gesehen (die Buehne blieb
 * in diesem Fenster versteckt, `isHidden`). Sitzungen (`/berater`, `/sitzung`, `/kanzler`) sind
 * ausdruecklich KEIN Beleg fuer den Klick eines Menschen.
 */
export const istMenschenAbsender = (von: unknown): boolean => von === 'rafael' || (typeof von === 'string' && /^partner:[a-z0-9][a-z0-9-]*$/.test(von));
/** Die Nachricht, die der Server zu einem Vorschlags-Auftrag anlegt — Ableitung aus der Auftragskennung, gleich wie in guide-buehnen-routen.ts (`/vorschlaege/antworten`). */
export const vorschlagNachrichtId = (auftragId: string): string => `n-vorschlaege-${auftragId.toLowerCase().replace(/[^a-z0-9_-]/g, '-').slice(0, 40)}`;
export function confirmed(job: Job, stage: Stage): boolean {
  if (job.close) return stage.abschluss?.art === 'erledigt';
  /* Gegen `raum_gesetzt`, nicht gegen `raum`: `raum` ist der GERECHNETE Raum und steht
   * auf 'sitzungssaal', solange an der Buehne jemand arbeitet — ein Abgleich dagegen
   * haette den Zug nie als bestaetigt gesehen. `raum_gesetzt` ist das, was Rafaels
   * Klick wirklich gespeichert hat (Befund 20.09.2026 zur Raumdivergenz). */
  if (job.raum) return (stage.raum_gesetzt ?? stage.raum) === job.raum;
  if (job.vorschlaege) return !!stage.nachrichten?.some(n => n.id === vorschlagNachrichtId(job.id) && istMenschenAbsender(n.von));
  if (job.text !== undefined) return !!stage.nachrichten?.some(n => n.id === job.id && istMenschenAbsender(n.von) && n.text === job.text);
  return !!job.choices?.every(c => { const a = stage.entscheidungen.find(e => e.id === c.eid)?.antwort;
    return istMenschenAbsender(a?.von) && (c.freitext !== undefined ? a.art === 'freitext' && a.text === c.freitext : a.kuerzel === c.kuerzel && (a.notiz ?? '') === (c.notiz ?? '')); });
}
/** Persist before navigation. Retries reconcile the original question and answer;
 * message IDs and the server's one-answer rule prevent duplicate effects. */
/*
 * DIE BELEGFRIST IST DIE GROSSZUEGIGSTE, NICHT DIE KNAPPSTE.
 *
 * Rafael, 21.09.2026: *"wieso gibts da immer noch signal timed out felder die nicht
 * abgearbeitet wurden?"* — die Arbeit war jedesmal getan (147 von 147 Abschluessen
 * ausgefuehrt, 0 blocked); nur der BELEG kam nicht rechtzeitig zurueck, und der
 * `catch` unten schreibt dann `uncertain` mit dem Wortlaut von AbortSignal.timeout.
 * Eine rote Zeile auf erledigter Arbeit ist schlimmer als eine langsame gruene.
 *
 * Gemessen am laufenden Server (21.09.2026 06:1xZ, ueber HTTP): der Belegweg kostet
 * im Leerlauf 70 ms, waehrend sechs `GET /buehnen?alle=1` laufen aber 46.522 ms —
 * er verhungert hinter den Sammelrouten in derselben Node-Schlange. Die eigentliche
 * Abhilfe steht serverseitig (`lib/guide-listen-cache.ts`); diese Frist ist der
 * zweite Riegel, damit ein einzelner Ausreisser nicht sofort wieder rot wird.
 *
 * Bewusst nicht laenger als die Frist des POST daneben: wer hier laenger wartet als
 * das Absenden selbst, wartet auf einen Beleg fuer etwas, das noch gar nicht
 * abgeschickt sein muesste.
 */
const BELEG_FRIST_MS = 30000;

/*
 * RAFAELS EINGABE UEBERHOLT DIE HINTERGRUNDABFRAGEN IM BROWSER (23.09.2026).
 *
 * Gemessen am Fall b-muehl-bericht-staging-20260923 (cui-usage.jsonl, Seite 96c6…):
 * zwischen 12:16 und 12:22 kamen die Anfragen dieses Fensters mit wachsendem Rueckstand
 * beim Server an, zuletzt 131 s. Der Server selbst beantwortete in derselben Zeit
 * jede protokollierte Anfrage in 3–12 s (guide-langsame-anfragen.jsonl); der Sende-POST
 * von 12:20:05 taucht dort gar nicht auf. Er stand also im Browser an, hinter den
 * regelmaessigen Abfragen (HTTP/1.1: sechs Verbindungen je Host fuer alles in diesem
 * Fenster). `priority: 'high'` hier und `'low'` an den Abfragen im Takt (Board, Hofliste,
 * Nutzungsmessung) laesst Chromium seinen Klick zuerst auf die naechste freie
 * Verbindung setzen. Der Server wird dadurch nicht schneller — nur die Schlange davor
 * ist fuer ihn keine mehr.
 */
/*
 * KEIN LESBARER BELEG IST 404 — ODER 403 (Befund 24.–30.09.2026, Haus Partner). Der Eingangsbeleg
 * `GET …/auftraege/:id` gehoert Rafael (`wirklichRafael`); eine Partnerin bekommt dort 403, und ihre
 * Eingaben erzeugen ohnehin nie einen Auftrag. Bis hierher warf der Abgleich bei 403 und setzte den
 * Auftrag auf `uncertain` — alle 15 s, tagelang, ohne je den Eingangsstand der Buehne zu lesen.
 */
/*
 * ARCHIVKOPIEN (`-abgewiesen:`, `-ueberholt:`, `-entwurf:`) LIEST KEIN CODE — sie sind Spur fuer
 * die Fehlersuche. Bis 30.09.2026 wuchsen sie unbegrenzt und mit voller Buehnenkopie. Jetzt:
 * schlank, hoechstens `MAX_ARCHIV` je Art, und nie ein Grund, an dem ein Klick scheitert.
 */
const MAX_ARCHIV = 20;
export type EingabeFehler = { stageId: string; meldung: string; speicherVoll: boolean;
  /** Kein Fehler, sondern Auskunft, warum die Buehne wieder offen vor ihm steht (umgezogene Frage). */
  hinweis?: boolean };
/** Der Guide (`useEntwurf`) laedt den Entwurf neu, wenn die Schlange ihn vorbelegt hat. */
export const ENTWURF_VORBELEGT = 'guide-entwurf-vorbelegt';
/** Belegt EIN Feld eines Entwurfs vor — nie ueber eine schon getippte/gewaehlte Eingabe hinweg. */
export function vorbelegeEntwurf(store: Store, stageId: string, art: 'wahl' | 'notiz', eid: string, wert: string): boolean {
  const schluessel = `guide-entwurf:${stageId}:${art}`;
  const roh = store.getItem(schluessel);
  const entwurf = roh ? JSON.parse(roh) : {};
  if (!entwurf || typeof entwurf !== 'object' || Array.isArray(entwurf)) throw Error(`Entwurf ${schluessel} ist kein Objekt.`);
  if (typeof entwurf[eid] === 'string' && entwurf[eid]) return false;
  entwurf[eid] = wert;
  store.setItem(schluessel, JSON.stringify(entwurf));
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ENTWURF_VORBELEGT, { detail: { schluessel } }));
  return true;
}
const keinBeleg = (status: number) => status === 404 || status === 403;
/*
 * ALTE ROTE KARTEN RAEUMEN SICH AUF, WENN IHR GRUND ERLEDIGT IST (Befund 01.10.2026, Kurt Engelmann).
 *
 * 25.–29.09.2026 schickte das Guide-Fenster jede Partner-Antwort mit `von: 'rafael'`, und der Server
 * wies sie mit 403 ab („… kam aber nicht ueber Rafaels eigene Anmeldung an …"). Seit 30.09. schreibt
 * das Partner-Tor `von` auf `partner:<id>` um (1f7aa69e3) — die alten Auftraege lagen aber weiter im
 * localStorage der Partner, `pump()` fasste sie nie mehr an, und die Karten im Warteraum standen rot,
 * mit einem Text, der Partner an Rafael verweist. Jetzt liest die Schlange fuer eine NIE GESPEICHERTE
 * Eingabe gelegentlich den Eingangsstand der Buehne und entscheidet (`abgewiesenLage`):
 *  - Buehne geschlossen oder jede Frage inzwischen beantwortet/vom Tisch -> Auftrag wird abgelegt
 *    (Archivkopie `-ueberholt:`, Nutzungsereignis `input_superseded` mit Grund — nichts verschwindet
 *    spurlos, und die Karte bleibt sichtbar, wenn sie noch Offenes hat).
 *  - Abweisung wegen der Rafael-Unterschrift und die Frage ist unveraendert offen -> EINMAL neu
 *    eingereicht; das Tor setzt jetzt den richtigen Namen.
 *  - sonst bleibt sie stehen, mit einem Text ohne Verweis auf Rafael (`ABGEWIESEN_PARTNER_TEXT`).
 */
export const istRafaelUnterschriftAbweisung = (fehler: unknown) => typeof fehler === 'string' && /nicht ueber Rafaels eigene Anmeldung/.test(fehler);
export const ABGEWIESEN_PARTNER_TEXT = 'Ein inzwischen behobener Anmeldefehler hat sie abgewiesen; gespeichert wurde nichts. Bitte an der Bühne den aktuellen Stand ansehen und dort neu antworten — oder diese Zeile verwerfen.';
/*
 * EIN ABGEWIESENER ABSCHLUSS SAGT, WARUM — UND WAS JETZT (Befund 05.10.2026, Mo Allagha).
 * Sein „Erledigt" an zwei eigenen Buehnen bekam 403 „Nur Rafael beauftragt diesen Abschluss."; die
 * Zeile blieb mit dem rohen Serverwortlaut stehen, ohne dass klar war, ob die Buehne nun zu ist und
 * was er tun kann. Jetzt steht ein deutscher Satz da (Grund + Ausweg); der Serverwortlaut bleibt in
 * `serverFehler` nachlesbar. Die Zeile bleibt „Erneut senden"-faehig — gesendet wird nur auf Klick.
 */
const ABSCHLUSS_RECHT = /Nur Rafael|gehoert nicht|gehört nicht|Zeugnis|erklaert eine Buehne nur|erklärt eine Bühne nur/i;
export function abschlussAbgewiesenText(status: number, serverError?: string): string {
  const nichts = 'Gespeichert wurde nichts, die Bühne ist weiter offen.';
  if (status === 403 && (!serverError || ABSCHLUSS_RECHT.test(serverError)))
    return `Diese Bühne darf nur Rafael oder ihr Eigentümer abschließen, und der Server hat diesen Klick nicht als den des Eigentümers erkannt. ${nichts} `
      + 'Ist es deine eigene Bühne, versuche es mit „Erneut senden“; sonst bitte Rafael, sie abzuschließen — oder verwirf diese Zeile.';
  if (status === 409)
    return 'Für diese Bühne läuft schon ein Abschluss; dieser zweite Klick wurde nicht gespeichert. '
      + 'Bitte kurz warten und den Stand der Bühne ansehen; diese Zeile kannst du dann verwerfen.';
  return `Der Server hat den Abschluss abgewiesen (HTTP ${status}${serverError ? `: ${serverError}` : ''}). ${nichts} `
    + '„Erneut senden“ versucht es noch einmal; hilft das nicht, bitte Rafael Bescheid geben — oder verwirf diese Zeile.';
}
/*
 * „AUF SPAETER" AN EINER BUEHNE, DIE NOCH IM BAU IST (Sahori Seefoo, Screenshot 07.10.2026).
 * Der Berater hatte die Nachfolgebuehne um 09:08Z angelegt, ihr Fenster (Rezept) kam erst 11:42Z.
 * Ihr Klick dazwischen bekam 409 „hat kein Rezept — kein Fenster ... fuer Rafael ... Erst ein
 * Rezept geben (Kacheln)" — eine Anweisung an den Berater, in Rafaels Namen, auf Deutsch, an
 * sie gerichtet. Jetzt steht da, was los ist und was sie tun kann; der Serverwortlaut bleibt in
 * `serverFehler` nachlesbar.
 */
export function raumAbgewiesenText(status: number, serverError?: string): string | null {
  if (status === 409 && serverError && /kein Rezept/i.test(serverError))
    return 'Diese Bühne ist noch im Bau: ihr Berater hat ihr Fenster noch nicht fertig. Erst danach lässt sie sich auf später legen. '
      + 'Gespeichert wurde nichts. Du kannst diese Zeile verwerfen und es später noch einmal versuchen. '
      + '(This stage is still being built; it can be put aside once its adviser has finished it. Nothing was saved — dismiss this line and try again later.)';
  return null;
}
/** Altzeilen vor dieser Fassung: „Vom Server abgewiesen (HTTP 403), nicht gespeichert: <Wortlaut>". */
function rohAbweisung(fehler: unknown): { status: number; wortlaut?: string } | null {
  const m = typeof fehler === 'string' ? /^Vom Server abgewiesen \(HTTP (\d{3})\), nicht gespeichert: ([\s\S]*)$/.exec(fehler) : null;
  if (!m) return null;
  return { status: Number(m[1]), wortlaut: m[2] === 'ohne Begründung' ? undefined : m[2] };
}
/** Nur Eingaben, die der Server nachweislich NICHT gespeichert hat. */
const nieGespeichert = (j: Job) => j.state === 'blocked' && !j.serverAccepted && (!!j.rejected || istRafaelUnterschriftAbweisung(j.error) || istRafaelUnterschriftAbweisung(j.serverFehler));
/** So oft liest eine abgewiesene Eingabe den Eingangsstand nach (beim Laden der Seite sofort). */
const ABGEWIESEN_PRUEF_ABSTAND_MS = 5 * 60_000;
/*
 * EINE UMGEZOGENE FRAGE REISST DIE UEBRIGEN ANTWORTEN NICHT MIT (Rafael, 10.10.2026: „kosmischer Fehler").
 *
 * Gemessen an b-energy-kontextverwaltung-probleme-und-ag-20261010: der Berater stellte um 09:08:08Z
 * `e-kv-kuerzen-nur-mit-ki-2` neu und vermerkte die alte Fassung um 09:08:30.8Z als umgezogen. Rafaels
 * Klick mit sechs Antworten ging um 09:08:31.7Z hinaus, 0,85 s spaeter, bevor das Nachladen der
 * offenen Buehne (400 ms Buendelung + Abruf) angekommen war. Das Buendel ist am Server atomar
 * (`/entscheidungen/antworten`): die eine Antwort auf die alte Kennung brachte 409, und KEINE der
 * anderen fuenf wurde gespeichert. „Erneut senden" schickte denselben Auftrag mit derselben alten
 * Kennung und bekam wieder 409. Die rote Zeile stand fuer immer, weil `abgewiesenLage` den Fall
 * nicht kannte.
 *
 * Jetzt liest die Schlange nach einer Abweisung den Eingangsstand (sofort, nicht erst nach fuenf
 * Minuten) und loest den Umzug auf — Grundlage ist der Datensatz `umgezogen` an der Frage, nicht der
 * Fehlertext:
 *  - die Wahl auf die alte Fassung wird an der neuen VORBELEGT (Entwurf), nie abgeschickt: die neue
 *    Fassung kann anders lauten, und antworten muss Rafael selbst. Gibt es sein Kuerzel dort nicht
 *    mehr, wird nichts vorbelegt und der Hinweis sagt es;
 *  - die Antworten auf die unveraenderten, weiter offenen Fragen gehen ohne die umgezogene erneut
 *    hinaus (das waren seine Klicks, gespeichert wurde davon nichts);
 *  - eine inzwischen geaenderte Frage wird ebenfalls nur vorbelegt;
 *  - der alte Auftrag verschwindet aus der Schlange (Archivkopie `-ueberholt:`), am Knopf der Buehne
 *    steht ein Hinweis, was passiert ist und was zu tun bleibt.
 */
export type Umzug = { choice: Choice; nach: { buehne: string; entscheidung: string } };
export type AbgewiesenLage =
  | { art: 'erledigt'; grund: 'buehne_geschlossen' | 'frage_beantwortet' }
  | { art: 'neu'; choices: Choice[] }
  | { art: 'umgezogen'; umzuege: Umzug[]; senden: Choice[]; vorbelegen: Choice[] }
  | { art: 'bleibt' };
export function abgewiesenLage(job: Job, stand: Stage): AbgewiesenLage {
  if (stand.abschluss?.art) return { art: 'erledigt', grund: 'buehne_geschlossen' };
  if (!job.choices?.length) return { art: 'bleibt' };
  const weg = (e: any) => !e || e.zustand === 'zurueckgezogen' || e.zustand === 'umgezogen';
  const offen = job.choices.filter(c => { const e = stand.entscheidungen?.find((x: any) => x.id === c.eid); return !weg(e) && !e.antwort; });
  const istUnveraendert = (c: Choice) => {
    const e = stand.entscheidungen.find((x: any) => x.id === c.eid); const vorher = job.stage.entscheidungen.find(x => x.id === c.eid);
    return !!vorher && question(e) === question(vorher);
  };
  const umzuege: Umzug[] = job.choices.flatMap(c => {
    const u = stand.entscheidungen?.find((x: any) => x.id === c.eid)?.umgezogen;
    return u && typeof u.buehne === 'string' && typeof u.entscheidung === 'string' ? [{ choice: c, nach: { buehne: u.buehne, entscheidung: u.entscheidung } }] : [];
  });
  if (umzuege.length) return { art: 'umgezogen', umzuege, senden: offen.filter(istUnveraendert), vorbelegen: offen.filter(c => !istUnveraendert(c)) };
  if (!offen.length) return { art: 'erledigt', grund: 'frage_beantwortet' };
  // Ein Rueckzug darf die weiterhin gueltigen Antworten desselben Buendels nicht blockieren.
  if (job.choices.some(c => stand.entscheidungen?.some(e => e.id === c.eid && e.zustand === 'zurueckgezogen')))
    return { art: 'umgezogen', umzuege: [], senden: offen.filter(istUnveraendert), vorbelegen: offen.filter(c => !istUnveraendert(c)) };
  const unveraendert = offen.every(istUnveraendert);
  if (unveraendert && !job.neuEingereicht && (istRafaelUnterschriftAbweisung(job.error) || istRafaelUnterschriftAbweisung(job.serverFehler))) return { art: 'neu', choices: offen };
  return { art: 'bleibt' };
}
/*
 * KEINE WAHL VERSCHWINDET STILL (Zweitpruefung UI1R, 10.10.2026). Seit dem 22.09. darf jede Sitzung
 * jede Frage beantworten; kommt ihre Antwort Sekunden vor Rafaels Klick an, weist der Server sein
 * Buendel mit 409 ab und der Abgleich legt den Auftrag ab. Fuer jede Wahl, die dabei nicht mehr
 * gesendet wird, weil die Frage anders beantwortet, zurueckgezogen, geloescht oder die Buehne
 * abgeschlossen ist, steht ein Satz im Hinweis am Knopf. Umgezogene, neu gesendete und vorbelegte
 * Wahlen (`ausgenommen`) sagt `loeseUmzug` selbst an.
 */
export function verworfeneWahlen(job: Job, stand: Stage, ausgenommen: Set<string> = new Set()): string[] {
  if (stand.abschluss?.art) {
    const was = job.choices?.length ? (job.choices.length === 1 ? 'deine Wahl wurde' : `deine ${job.choices.length} Antworten wurden`) : job.vorschlaege ? 'deine Vorschlags-Antworten wurden' : job.text !== undefined ? 'deine Nachricht wurde' : null;
    return was ? [`Die Bühne „${stand.titel ?? stand.id}“ ist inzwischen abgeschlossen; ${was} nicht gespeichert.`] : [];
  }
  const saetze: string[] = [];
  for (const c of job.choices ?? []) {
    if (ausgenommen.has(c.eid)) continue;
    const e: any = stand.entscheidungen?.find((x: any) => x.id === c.eid);
    const titel = `„${e?.frage ?? job.stage.entscheidungen.find(x => x.id === c.eid)?.frage ?? c.eid}“`;
    const wahl = c.freitext !== undefined ? 'deine freie Antwort' : c.kuerzel ? `deine Wahl „${c.kuerzel}“` : 'deine Notiz';
    if (!e) { saetze.push(`${titel} gibt es nicht mehr; ${wahl} wurde nicht gespeichert.`); continue; }
    if (e.zustand === 'zurueckgezogen') { saetze.push(`${titel} wurde zurückgezogen; ${wahl} wurde nicht gespeichert.`); continue; }
    const a = e.antwort; if (!a || e.zustand === 'umgezogen') continue;
    const gleich = c.freitext !== undefined ? a.art === 'freitext' && a.text === c.freitext : a.kuerzel === c.kuerzel && (a.notiz ?? '') === (c.notiz ?? '');
    if (!gleich) saetze.push(`${titel} war schon beantwortet (${a.kuerzel ? `„${a.kuerzel}“` : 'frei'}${a.von && !istMenschenAbsender(a.von) ? `, von ${a.von}` : ''}); ${wahl} wurde nicht gespeichert.`);
  }
  return saetze;
}
/*
 * GESPEICHERT HEISST FUER DEN BROWSER: ERLEDIGT — bei Nachricht und Antwort (18.10.2026).
 *
 * Der Server legt fuer Rafaels Nachricht/Antwort einen dauerhaften Auftrag an, speichert das
 * Wort am Objekt und antwortet erst DANN mit `gespeichert: true` (Status `pending`). Die
 * Zustellung an den Berater ist ab da seine Sache: er wiederholt sie selbst (`durableWorker`,
 * `nachholeBlockierte`, Duplikate ueber die Auftragskennung) und meldet ein endgueltiges
 * Scheitern in den Sitzungssaal (`meldeSitzungssaal`, guide-buehnen-routen.ts) — nicht an
 * Rafael. Bis hierher hielt der Browser den Auftrag, bis der Beleg `done` zurueckkam; jede
 * langsame Beleg-Abfrage wurde so zu „Zustellung unbestätigt" und sperrte die Buehne.
 * Abschluss und Raumwechsel bleiben beim alten Weg: dort IST der Beleg das Ergebnis.
 */
const serverTraegtZustellung = (job: Job) => !job.close && !job.raum;
const mitVorrang: typeof fetch = (input, init) => fetch(input, { priority: 'high', ...init });

/** Form-Pruefung statt Typ-Behauptung: ein fremder oder kaputter Eintrag im gemeinsamen Speicher wird laut uebersprungen, nie als Auftrag gelesen. */
function istAuftrag(x: unknown): x is Job {
  return typeof x === 'object' && x !== null && 'id' in x && typeof x.id === 'string' && 'stage' in x && typeof x.stage === 'object' && x.stage !== null && 'id' in x.stage && typeof x.stage.id === 'string';
}

export class GuideSendQueue {
  jobs: Job[] = []; fault: string | null = null;
  /** Warum der letzte Klick an einer Buehne nicht angenommen wurde — gezeigt AM Knopf (GuideAbsendeFehler). */
  eingabeFehler: EingabeFehler | null = null;
  private handled = new Map<string, Job>();
  private checked = new Map<string, number>();
  private checkSequence = 0;
  private closing = new Set<string>();
  private abgewiesenGeprueft = new Map<string, number>();
  isHidden(stage: Stage) { const pending = this.jobs.find(j => j.stage.id === stage.id); if (pending) return pending.state !== 'blocked'; const done = this.handled.get(stage.id); return !!done && (done.close || !confirmed(done, stage)); }
  private listeners = new Set<() => void>(); private running = new Set<string>(); private enabled = false;
  constructor(private store: Store, private key: string, private request: typeof fetch = mitVorrang) {
    this.refresh();
  }
  refresh = () => {
    try {
      const jobs: Job[] = [];
      for (let i = 0; i < this.store.length; i++) {
        const k = this.store.key(i); if (!k?.startsWith(this.key + ':')) continue;
        const j = JSON.parse(this.store.getItem(k)!);
        if (!j.id || !j.stage?.id || !['queued','sending','uncertain','blocked'].includes(j.state)) throw Error('Ungültiger Sendespeicher');
        jobs.push({ ...j, state: j.attempted && j.state !== 'blocked' && !this.running.has(j.id) ? 'uncertain' : j.state });
      }
      this.jobs = jobs; this.fault = null;
    } catch { this.fault = 'Der lokale Sendespeicher ist nicht lesbar. Die vorhandenen Eingaben bleiben erhalten.'; }
    for (const fn of this.listeners) fn();
  };

  /**
   * Auftraege aus dem gemeinsamen Speicher, die NICHT in dieser Schlange laufen — etwa die eines zweiten Fensters derselben Buehne. Nur lesend (sie werden hier nicht gesendet und
   * nicht angefasst); der Absenden-Knopf sperrt sich damit, solange das andere Fenster noch sendet. Nicht lesbare Eintraege werden laut gemeldet und uebersprungen.
   */
  fremdeAuftraege(): Job[] {
    const aus: Job[] = [];
    for (let i = 0; i < this.store.length; i++) {
      const k = this.store.key(i);
      if (!k?.startsWith(this.key + ':')) continue;
      const roh = this.store.getItem(k);
      if (!roh) continue;
      let j: unknown;
      try { j = JSON.parse(roh); } catch (e) { console.error(`[GuideSendQueue] Auftrag ${k} nicht lesbar:`, e); continue; }
      if (!istAuftrag(j)) { console.error(`[GuideSendQueue] Auftrag ${k} hat nicht die erwartete Form — uebersprungen.`); continue; }
      if (!this.jobs.some(x => x.id === j.id)) aus.push(j);
    }
    return aus;
  }
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  /*
   * DER SERVER HAT DIE EINGABE GESPEICHERT (ASSIOEFFNEN, 13.10.2026). Feuert genau einmal je
   * Auftrag beim Uebergang auf `serverAccepted` bzw. bei `accept()` ohne vorherige Annahme —
   * nie fuer eine nur lokal eingereihte oder abgewiesene Eingabe. Nutzer: der Weckruf an den
   * Assistenten im Assistent-Modus. Die Zustellung an den Berater haengt nicht daran.
   */
  private gespeichertHoerer = new Set<(job: Job) => void>();
  onGespeichert = (fn: (job: Job) => void) => { this.gespeichertHoerer.add(fn); return () => { this.gespeichertHoerer.delete(fn); }; };
  private meldeGespeichert(job: Job) {
    for (const fn of this.gespeichertHoerer) { try { fn(job); } catch (e) { console.error('[GuideSendQueue] onGespeichert:', e); } }
  }
  snapshot = () => this.jobs;
  private save(jobs: Job[]) {
    // Each input has its own atomic key: a second Guide tab cannot overwrite it.
    for (const j of jobs) if (JSON.stringify(j) !== JSON.stringify(this.jobs.find(old => old.id === j.id))) schreibeMitPlatz(this.store, this.key + ':' + j.id, JSON.stringify(j));
    for (const j of this.jobs) if (!jobs.some(next => next.id === j.id)) this.store.removeItem(this.key + ':' + j.id);
    this.refresh();
  }
  private archiviere(art: 'abgewiesen' | 'ueberholt' | 'entwurf', job: Job) {
    const praefix = `${this.key}-${art}:`;
    const alte: string[] = [];
    for (let i = 0; i < this.store.length; i++) { const k = this.store.key(i); if (k?.startsWith(praefix) && k !== praefix + job.id) alte.push(k); }
    for (const k of alte.sort().slice(0, Math.max(0, alte.length - (MAX_ARCHIV - 1)))) this.store.removeItem(k);
    try { this.store.setItem(praefix + job.id, JSON.stringify(schlankerAuftrag(job))); }
    catch (e) { if (!istSpeicherVoll(e)) throw e; console.warn(`[guide-send] Archivkopie ${art}:${job.id} nicht abgelegt — Speicher voll. Der Klick selbst ist davon nicht betroffen.`); }
  }
  private meldeEingabeFehler(f: EingabeFehler | null) { this.eingabeFehler = f; this.jobs = [...this.jobs]; for (const fn of this.listeners) fn(); }
  vergissEingabeFehler() { this.meldeEingabeFehler(null); }
  /** „Speicher aufraeumen“ am Knopf: Wegwerf-Caches weg, Archive gekappt. Nie ein offener Auftrag. */
  raeumeSpeicherAuf() {
    const r = raeumeCachesAuf(this.store);
    const vorher = this.eingabeFehler;
    this.meldeEingabeFehler(null);
    usage('input_state', { action: 'speicher_aufgeraeumt', count: r.entfernt, stage_id: vorher?.stageId });
    return r;
  }
  enqueue(stage: Stage, choices?: Choice[], text?: string, close = false, raum?: string, vorschlaege?: VorschlagWahl[]) {
    try { const id = this.enqueueMitAufraeumen(stage, choices, text, close, raum, vorschlaege); if (this.eingabeFehler?.stageId === stage.id) this.meldeEingabeFehler(null); return id; }
    catch (e) {
      const speicherVoll = e instanceof SpeicherVollFehler || istSpeicherVoll(e);
      const meldung = e instanceof Error ? e.message : String(e);
      if (speicherVoll) usage('input_state', { stage_id: stage.id, action: 'speicher_voll', reason: e instanceof SpeicherVollFehler ? bilanzText(e.bilanz, 6) : bilanzText(speicherBilanz(this.store), 6) });
      this.meldeEingabeFehler({ stageId: stage.id, meldung, speicherVoll });
      throw e;
    }
  }
  /** Speicher voll: einmal selbst die Wegwerf-Caches raeumen und erneut sichern, statt ihn den Knopf druecken zu lassen. */
  private enqueueMitAufraeumen(stage: Stage, choices?: Choice[], text?: string, close = false, raum?: string, vorschlaege?: VorschlagWahl[]) {
    try { return this.enqueueRoh(stage, choices, text, close, raum, vorschlaege); }
    catch (e) {
      if (!(e instanceof SpeicherVollFehler || istSpeicherVoll(e))) throw e;
      const r = raeumeCachesAuf(this.store);
      usage('input_state', { stage_id: stage.id, action: 'speicher_aufgeraeumt', count: r.entfernt });
      return this.enqueueRoh(stage, choices, text, close, raum, vorschlaege);
    }
  }
  private enqueueRoh(stage: Stage, choices?: Choice[], text?: string, close = false, raum?: string, vorschlaege?: VorschlagWahl[]) {
    if (vorschlaege && !vorschlaege.length) throw Error('Vorschlags-Antworten ohne Wahl können nicht eingereiht werden.');
    if (this.fault) throw Error(this.fault);
    // Rafael, 15.09.2026: sein Abschluss-Klick ist der Not-Aus und muss IMMER
    // durchgehen, ueberschreibt jeden anderen Zustand — auch eine fuer dieselbe
    // Buehne bereits unterwegse (nicht-schliessende) Eingabe. Nur zwei Schliess-
    // Auftraege fuer dieselbe Buehne bleiben gesperrt (Server haelt diese Sperre
    // ohnehin: guide-buehnen-routen.ts wirft 409 'laeuft bereits ein Abschlussauftrag');
    // die aeltere, nicht-schliessende Eingabe bleibt dabei erhalten, nicht verworfen —
    // sie loest sich unabhaengig weiter auf oder bleibt sichtbar mit ihrem Fehler.
    /*
     * EINE ABGEWIESENE EINGABE SPERRT DIE BUEHNE NICHT (Befund David Steiner / Kurt Engelmann,
     * 25.09.2026): der Server hatte ihre Antwort mit 403 abgewiesen, der Auftrag blieb als
     * 'blocked' liegen, und jeder weitere Klick an derselben Buehne scheiterte an dieser Zeile —
     * still, weil die Guide-Ansicht nach dem Klick schon weitergesprungen war. Eine Eingabe, die
     * der Server nachweislich NICHT gespeichert hat (`rejected`, nie `serverAccepted`), wird von
     * der neuen ersetzt; ihr Wortlaut und ihr Fehler bleiben unter `-abgewiesen:` nachlesbar.
     */
    /*
     * KEINE SPERRE MEHR JE BUEHNE (Rafael, 18.10.2026: „was interessiert mich das ob die im
     * sendestatus ist — das ist für mich überhaupt keine info"). Bis hierher warf diese Stelle
     * „Für diese Bühne ist noch eine Eingabe unterwegs", solange die vorige Eingabe an derselben
     * Buehne nicht quittiert war. Gemessen 18.10. 18:39Z (b-raves-…): seine Nachricht war nach
     * 2,6 s beim Berater zugestellt, der Browser quittierte sie erst 43 s spaeter — dazwischen
     * war Textfeld und Knopf fuer ihn tot. Jetzt geht jede Eingabe sofort in die Schlange; die
     * Reihenfolge haelt `pump()` (`wartetAufVorgaenger`), nicht eine Sperre vor seinem Klick.
     */
    const abgewiesen = this.jobs.filter(j => j.stage.id === stage.id && j.rejected && !j.serverAccepted);
    for (const j of abgewiesen) this.archiviere('abgewiesen', j);
    const id = `n-${Date.now().toString(36)}-${Array.from(crypto.getRandomValues(new Uint32Array(2)), x => x.toString(36)).join('')}`;
    /* Streng steigend, auch bei zwei Eingaben in derselben Millisekunde — die Kennung taugt nicht als Reihenfolge (Zufallsteil). */
    const erstelltAm = Math.max(Date.now(), ...this.jobs.map(j => (j.erstelltAm ?? 0) + 1));
    this.save([...this.jobs.filter(j => close || !abgewiesen.includes(j)), { id, stage: schlankeBuehne(stage, choices), choices, ...(vorschlaege ? { vorschlaege } : {}), text, close, raum, attempted: false, state: 'queued', erstelltAm }]);
    usage('input_queued', { stage_id: stage.id, job_id: id, action: close ? 'close' : raum ? 'room' : text !== undefined ? 'message' : vorschlaege ? 'proposals' : 'choices', count: (vorschlaege ?? choices)?.length });
    void this.pump(); return id;
  }
  start() { this.enabled = true; void this.pump(); }
  stop() { this.enabled = false; }
  private update(id: string, patch: Partial<Job>) {
    if (patch.state) usage('input_state', { job_id: id, action: patch.state });
    const vorher = this.jobs.find(j => j.id === id);
    this.save(this.jobs.map(j => j.id === id ? { ...j, ...patch } : j));
    if (patch.serverAccepted && vorher && !vorher.serverAccepted) this.meldeGespeichert({ ...vorher, ...patch });
  }
  returnToDraft(id: string) { const job = this.jobs.find(j => j.id === id); if (!job || job.state !== 'blocked') return; this.archiviere('entwurf', job); this.save(this.jobs.filter(j => j.id !== id)); }
  retry(id: string) {
    const job = this.jobs.find(j => j.id === id);
    /* Nie gespeichert -> nichts abzugleichen: „Erneut“ schickt sie frisch (derselbe Auftrag, dieselbe
     * Kennung). Vorher lief sie in den Beleg-Abgleich, bekam dort 403 und stand ewig auf `uncertain`
     * (gemessen 24.–30.09.2026: 26.000 Zustandswechsel bei Sahori und Kurt, kein einziges Senden). */
    if (job?.rejected && !job.serverAccepted) { this.update(id, { state: 'queued', attempted: false, rejected: undefined, error: undefined }); void this.pump(); return; }
    this.update(id, { state: 'uncertain', error: undefined, retryRequested: this.jobs.some(j => j.id === id && j.serverAccepted) }); void this.pump();
  }
  /** Eine abgewiesene Eingabe ausdruecklich verwerfen (sie war nie gespeichert). */
  dismiss(id: string) { const job = this.jobs.find(j => j.id === id); if (!job?.rejected || job.serverAccepted) return; this.archiviere('abgewiesen', job); this.save(this.jobs.filter(j => j.id !== id)); }
  async pump() {
    if (!this.enabled || this.fault) return;
    for (const job of [...this.jobs].sort((a,b) => Number(a.attempted) - Number(b.attempted) || (this.checked.get(a.id) ?? 0) - (this.checked.get(b.id) ?? 0))) {
      if (this.running.size >= 3) break;
      if (this.running.has(job.id)) continue;
      if (nieGespeichert(job) && Date.now() - (this.abgewiesenGeprueft.get(job.id) ?? 0) < ABGEWIESEN_PRUEF_ABSTAND_MS) continue;
      if (job.close && !job.attempted && this.closing.size) continue;
      if (this.wartetAufVorgaenger(job)) continue;
      this.checked.set(job.id, ++this.checkSequence);
      if (nieGespeichert(job)) this.abgewiesenGeprueft.set(job.id, Date.now());
      this.running.add(job.id); void (nieGespeichert(job) ? this.pruefeAbgewiesen(job) : job.state === 'blocked' ? this.reconcileBlocked(job) : this.send(job)).finally(() => { this.running.delete(job.id); if (this.jobs.some(j => j.state === 'queued')) void this.pump(); });
    }
  }
  /*
   * REIHENFOLGE JE BUEHNE STATT SPERRE. Eine noch nie gesendete Eingabe wartet, solange an
   * derselben Buehne eine AELTERE Eingabe noch nicht vom Server gespeichert ist — der Server
   * reiht dann in Tipp-Reihenfolge ein. Sobald die aeltere gespeichert (oder endgueltig
   * abgewiesen) ist, geht die naechste sofort. Abschluesse folgen ihrer eigenen Regel oben.
   */
  private wartetAufVorgaenger(job: Job): boolean {
    if (job.attempted || job.close) return false;
    /* Altauftraege ohne `erstelltAm` (vor dieser Fassung) zaehlen als aelter. */
    const vor = (j: Job) => (j.erstelltAm ?? 0) < (job.erstelltAm ?? 0);
    return this.jobs.some(j => j.id !== job.id && j.stage.id === job.stage.id && !j.close && vor(j)
      && !j.serverAccepted && j.state !== 'blocked');
  }
  private accept(job: Job, superseded = false) {
    usage(superseded ? 'input_superseded' : 'input_confirmed', { stage_id: job.stage.id, job_id: job.id });
    this.checked.delete(job.id);
    this.handled.set(job.stage.id, job);
    const schonGemeldet = this.jobs.some(j => j.id === job.id && j.serverAccepted);
    this.save(this.jobs.filter(j => j.id !== job.id));
    if (!superseded && !schonGemeldet) this.meldeGespeichert(job);
    if (job.close) return;
    // Clear only the submitted draft; a newer typed sentence must survive.
    try {
      const clean = (kind: string, values: Record<string, string | undefined>) => {
        const key = `guide-entwurf:${job.stage.id}:${kind}`;
        const draft = JSON.parse(this.store.getItem(key) ?? '{}');
        for (const [id, value] of Object.entries(values)) {
          if (value === undefined || typeof draft[id] !== 'string') continue;
          // wahl: genau gleich; Texte (freies Wort, Notiz): getrimmt, und nur der gesendete Teil (Weitergetipptes bleibt) — siehe zieheGesendetAb.
          const rest = kind === 'wahl' || kind === 'vorschlag' ? (draft[id] === value ? '' : null) : zieheGesendetAb(value, draft[id]);
          if (rest === '') delete draft[id]; else if (rest !== null) draft[id] = rest;
        }
        this.store.setItem(key, JSON.stringify(draft));
        // Das offene Feld zeigt sonst weiter den schon gesendeten Text: die Eingabefelder laden den Entwurf neu (derselbe Weg wie beim Vorbelegen). Erst JETZT, bei der Quittung — nie vorher.
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ENTWURF_VORBELEGT, { detail: { schluessel: key } }));
      };
      if (job.text !== undefined) clean('freies-wort', { text: job.text });
      else if (job.vorschlaege) clean('vorschlag', Object.fromEntries(job.vorschlaege.map(v => [v.vid, v.kuerzel])));
      else { clean('wahl', Object.fromEntries(job.choices!.map(c => [c.eid, c.kuerzel]))); clean('notiz', Object.fromEntries(job.choices!.map(c => [c.eid, c.notiz ?? c.freitext]))); }
    } catch { /* Receipt is confirmed; retaining a draft cannot undo it. */ }
  }
  private obsoleteClose(job: Job, current: Stage): boolean {
    if (!job.close || current.id !== job.stage.id || current.abschluss?.art !== 'abgeloest') return false;
    // Der Klick ist gegenstandslos, nicht nachträglich als ausgeführt bestätigt.
    this.archiviere('ueberholt', { ...job, latest: current });
    this.accept(job, true);
    return true;
  }
  private async reconcileClosedStage(job: Job, path: string): Promise<boolean> {
    if (!job.close) return false;
    const response = await this.request(path + '/eingangsstand', { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
    if (!response.ok || !this.enabled || !this.jobs.some(j => j.id === job.id)) return false;
    const current = await response.json();
    if (current.id !== job.stage.id) return false;
    if (confirmed(job, current)) { this.accept(job); return true; }
    return this.obsoleteClose(job, current);
  }
  /** Siehe `abgewiesenLage`. Liest nur; sendet hoechstens EINMAL neu und nur bei offener, unveraenderter Frage. */
  private async pruefeAbgewiesen(job: Job) {
    try {
      const r = await this.request(`/api/guide/buehnen/${encodeURIComponent(job.stage.id)}/eingangsstand`, { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
      if (!r.ok || !this.enabled) return;
      const stand = await r.json();
      const aktuell = this.jobs.find(j => j.id === job.id);
      if (!aktuell || !nieGespeichert(aktuell) || stand?.id !== job.stage.id) return;
      if (confirmed(aktuell, stand)) { this.accept(aktuell); return; }
      const lage = abgewiesenLage(aktuell, stand);
      if (lage.art === 'erledigt') {
        this.archiviere('ueberholt', { ...aktuell, latest: stand });
        usage('input_superseded', { stage_id: aktuell.stage.id, job_id: aktuell.id, reason: lage.grund });
        this.checked.delete(aktuell.id); this.abgewiesenGeprueft.delete(aktuell.id);
        this.save(this.jobs.filter(j => j.id !== aktuell.id));
        const saetze = verworfeneWahlen(aktuell, stand);
        if (saetze.length) {
          const vorher = this.eingabeFehler?.hinweis && this.eingabeFehler.stageId === stand.id ? this.eingabeFehler.meldung + ' ' : '';
          this.meldeEingabeFehler({ stageId: stand.id, meldung: vorher + saetze.join(' '), speicherVoll: false, hinweis: true });
        }
        return;
      }
      if (lage.art === 'umgezogen') { await this.loeseUmzug(aktuell, stand, lage); return; }
      if (lage.art === 'neu') {
        usage('input_state', { stage_id: aktuell.stage.id, job_id: aktuell.id, action: 'neu_eingereicht', count: lage.choices.length });
        this.abgewiesenGeprueft.delete(aktuell.id);
        this.update(aktuell.id, { state: 'queued', attempted: false, rejected: undefined, error: undefined, neuEingereicht: true,
          serverFehler: aktuell.serverFehler ?? aktuell.error, choices: lage.choices, stage: schlankeBuehne(aktuell.stage, lage.choices) });
        return;
      }
      if (istRafaelUnterschriftAbweisung(aktuell.error))
        this.update(aktuell.id, { rejected: true, error: ABGEWIESEN_PARTNER_TEXT, serverFehler: aktuell.serverFehler ?? aktuell.error });
      else if (aktuell.close) {
        /* Ein schon abgewiesener Abschluss aus der Zeit vor der Erklaerung bekommt sie nachtraeglich —
         * nur der Text aendert sich, die Zeile bleibt „Erneut senden"-faehig, nichts wird gesendet. */
        const roh = rohAbweisung(aktuell.error);
        if (roh) this.update(aktuell.id, { error: abschlussAbgewiesenText(roh.status, roh.wortlaut), serverFehler: aktuell.serverFehler ?? aktuell.error });
      }
    } catch { /* Lesen gescheitert: die Karte bleibt, wie sie ist — nie verworfen. */ }
  }
  /** Siehe Kommentar an `abgewiesenLage`. Sendet nur die unveraenderten offenen Antworten erneut; alles andere wird Entwurf. */
  private async loeseUmzug(job: Job, stand: Stage, lage: Extract<AbgewiesenLage, { art: 'umgezogen' }>) {
    const staende = new Map<string, Stage>([[stand.id, stand]]);
    for (const { nach } of lage.umzuege) {
      if (staende.has(nach.buehne)) continue;
      const r = await this.request(`/api/guide/buehnen/${encodeURIComponent(nach.buehne)}/eingangsstand`, { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
      if (!r.ok) throw Error(`Stand der Zielbühne ${nach.buehne} nicht lesbar (HTTP ${r.status}).`);
      staende.set(nach.buehne, await r.json());
    }
    const aktuell = this.jobs.find(j => j.id === job.id);
    if (!this.enabled || !aktuell || !nieGespeichert(aktuell)) return;
    const weg = (e: any) => !e || e.zustand === 'zurueckgezogen' || e.zustand === 'umgezogen';
    const gesendet = new Set(lage.senden.map(c => c.eid));
    const saetze: string[] = [];
    let hinweisAn = stand.id;
    for (const { choice, nach } of lage.umzuege) {
      const alt = stand.entscheidungen.find((x: any) => x.id === choice.eid);
      const ziel = staende.get(nach.buehne)?.entscheidungen?.find((x: any) => x.id === nach.entscheidung);
      const titel = `„${alt?.frage ?? choice.eid}“`;
      if (nach.buehne === stand.id && gesendet.has(nach.entscheidung)) { saetze.push(`${titel} ist neu gestellt; deine Antwort auf die neue Fassung geht mit.`); continue; }
      if (weg(ziel) || ziel.antwort) { saetze.push(`${titel} ist nach ${nach.buehne}/${nach.entscheidung} umgezogen, dort aber schon beantwortet oder vom Tisch — deine Wahl wurde nicht übertragen.`); continue; }
      if (nach.buehne !== stand.id) hinweisAn = nach.buehne;
      const kuerzel = choice.kuerzel && ziel.optionen?.some((o: any) => o.kuerzel === choice.kuerzel) ? choice.kuerzel : undefined;
      const notiz = choice.freitext ?? choice.notiz;
      const belegt = [kuerzel && vorbelegeEntwurf(this.store, nach.buehne, 'wahl', nach.entscheidung, kuerzel), notiz && vorbelegeEntwurf(this.store, nach.buehne, 'notiz', nach.entscheidung, notiz)].some(Boolean);
      const ort = nach.buehne === stand.id ? '' : ` (Bühne ${nach.buehne})`;
      saetze.push(choice.kuerzel && !kuerzel
        ? `${titel} ist inzwischen neu gestellt${ort}; deine Wahl „${choice.kuerzel}“ gibt es in der neuen Fassung nicht mehr${belegt ? ', deine Notiz steht dort' : ''}. Bitte neu wählen.`
        : `${titel} ist inzwischen neu gestellt${ort}; ${belegt ? 'deine Wahl steht dort vorausgewählt' : 'dort steht schon dein eigener Entwurf'}. Bitte die neue Fassung lesen und absenden.`);
    }
    for (const c of lage.vorbelegen) {
      if (c.kuerzel) vorbelegeEntwurf(this.store, stand.id, 'wahl', c.eid, c.kuerzel);
      const notiz = c.freitext ?? c.notiz; if (notiz) vorbelegeEntwurf(this.store, stand.id, 'notiz', c.eid, notiz);
      saetze.push(`„${stand.entscheidungen.find((x: any) => x.id === c.eid)?.frage ?? c.eid}“ hat sich seither geändert; deine Wahl steht als Entwurf da.`);
    }
    saetze.push(...verworfeneWahlen(aktuell, stand, new Set([...lage.umzuege.map(u => u.choice.eid), ...lage.senden.map(c => c.eid), ...lage.vorbelegen.map(c => c.eid)])));
    if (lage.senden.length) saetze.push(`${lage.senden.length === 1 ? 'Deine Antwort auf die übrige Frage wird' : `Deine ${lage.senden.length} Antworten auf die übrigen Fragen werden`} jetzt gesendet.`);
    this.archiviere('ueberholt', { ...aktuell, latest: stand });
    this.checked.delete(aktuell.id); this.abgewiesenGeprueft.delete(aktuell.id);
    usage('input_superseded', { stage_id: aktuell.stage.id, job_id: aktuell.id, reason: lage.umzuege.length ? 'frage_umgezogen' : 'frage_zurueckgezogen', count: lage.senden.length });
    if (lage.senden.length)
      this.update(aktuell.id, { state: 'queued', attempted: false, rejected: undefined, error: undefined,
        serverFehler: aktuell.serverFehler ?? aktuell.error, choices: lage.senden, stage: schlankeBuehne(aktuell.stage, lage.senden) });
    else this.save(this.jobs.filter(j => j.id !== aktuell.id));
    this.meldeEingabeFehler({ stageId: hinweisAn, meldung: saetze.join(' '), speicherVoll: false, hinweis: true });
  }
  /** Old failures are not current server state. Reconcile them without ever
   * replaying an action; only an explicit retry may submit again. */
  private async reconcileBlocked(job: Job) {
    const path = `/api/guide/buehnen/${encodeURIComponent(job.stage.id)}`;
    try {
      const response = await this.request(path + '/auftraege/' + encodeURIComponent(job.id), { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
      if (!this.enabled || !this.jobs.some(j => j.id === job.id && j.state === 'blocked')) return;
      if (response.ok) {
        const receipt = await response.json();
        if (receipt.auftragId !== job.id) return;
        if (receipt.status === 'done' || serverTraegtZustellung(job)) { this.accept(job); return; }
        if (receipt.status === 'blocked' && await this.reconcileClosedStage(job, path)) return;
        if (receipt.status === 'pending' || receipt.status === 'running')
          this.update(job.id, { serverAccepted: true, state: 'uncertain', error: job.close ? 'Abschluss läuft.' : 'Antwort gespeichert; Zustellung läuft.' });
        return;
      }
      if (!keinBeleg(response.status)) return;
      if (job.serverAccepted && serverTraegtZustellung(job)) { this.accept(job); return; }
      if (job.serverAccepted) { await this.reconcileClosedStage(job, path); return; }
      const current = await this.request(path + '/eingangsstand', { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
      if (!current.ok || !this.enabled || !this.jobs.some(j => j.id === job.id && j.state === 'blocked')) return;
      const stand = await current.json();
      // Eine zurueckgezogene/umgezogene Frage bekommt nie eine Antwort an dieser
      // Stelle (Serverregel) — ohne diesen Zweig bliebe der Auftrag fuer immer
      // 'blocked', weil confirmed() nie zutrifft. Siehe ueberholteWahl().
      if (this.obsoleteClose(job, stand)) return;
      if (confirmed(job, stand) || this.ueberholteWahl(job, stand).obsolete) this.accept(job);
    } catch { /* A failed read cannot undo an input or authorize its replay. */ }
  }
  /** Ist die Frage hinter mind. einer Wahl noch aktiv, aber Antwort/Inhalt
   *  abweichend (`strittig`), braucht es Rafaels Blick. Ist sie fuer JEDE Wahl vom
   *  Tisch (geloescht, zurueckgezogen oder anderswohin umgezogen — Zustaende, die
   *  laut Server nie mehr eine Antwort bekommen), ist der Auftrag `obsolete`:
   *  gegenstandslos, kein Fehler. */
  private ueberholteWahl(job: Job, current: Stage): { obsolete: boolean; strittig: boolean } {
    if (!job.choices?.length) return { obsolete: false, strittig: false };
    const weg = (e: any) => !e || e.zustand === 'zurueckgezogen' || e.zustand === 'umgezogen';
    const lagen = job.choices.map(c => ({
      e: current.entscheidungen?.find((e: any) => e.id === c.eid),
      before: job.stage.entscheidungen.find(e => e.id === c.eid),
    }));
    const obsolete = lagen.every(({ e }) => weg(e));
    const strittig = !obsolete && lagen.some(({ e, before }) => e && before && !weg(e) && (e.antwort || question(e) !== question(before)));
    return { obsolete, strittig };
  }
  private async send(job: Job) {
    const path = `/api/guide/buehnen/${encodeURIComponent(job.stage.id)}`;
    try {
      if (job.retryRequested) {
        const response = await this.request(path + '/auftraege/' + encodeURIComponent(job.id) + '/wiederholen', {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}', signal: AbortSignal.timeout(BELEG_FRIST_MS),
        });
        this.update(job.id, { retryRequested: false });
        if (!response.ok) {
          const d = await response.json().catch(() => null);
          this.update(job.id, { state: response.status === 409 || response.status === 403 ? 'blocked' : 'uncertain', error: d?.error ?? 'Wiederholung noch nicht bestätigt.' }); return;
        }
      }
      if (job.attempted) {
        const receipt = await this.request(path + '/auftraege/' + encodeURIComponent(job.id), { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
        if (receipt.ok) {
          const d = await receipt.json();
          if (d.auftragId !== job.id) throw Error('Serverbeleg passt nicht zu deiner Eingabe.');
          if (d.status === 'done' || serverTraegtZustellung(job)) { this.accept(job); return; }
          if (d.status === 'blocked' && await this.reconcileClosedStage(job, path)) return;
          this.update(job.id, { serverAccepted: true, state: d.status === 'blocked' ? 'blocked' : 'uncertain',
            error: d.status === 'blocked' ? d.error : job.close ? 'Abschluss läuft.' : 'Antwort gespeichert; Zustellung läuft.' });
          return;
        }
        if (!keinBeleg(receipt.status)) throw Error(`Serverbeleg noch nicht prüfbar (HTTP ${receipt.status}).`);
        if (job.serverAccepted) {
          if (serverTraegtZustellung(job)) { this.accept(job); return; }
          if (await this.reconcileClosedStage(job, path)) return;
          throw Error('Gespeicherter Auftrag ist momentan nicht lesbar; kein erneutes Senden.');
        }
        const r = await this.request(path + '/eingangsstand', { signal: AbortSignal.timeout(BELEG_FRIST_MS) });
        if (!r.ok) throw Error(`Stand noch nicht prüfbar (HTTP ${r.status}).`);
        const current = await r.json();
        if (this.obsoleteClose(job, current)) return;
        if (confirmed(job, current)) { this.accept(job); return; }
        const { obsolete, strittig } = this.ueberholteWahl(job, current);
        if (obsolete) { this.accept(job); return; }
        if (strittig) {
          this.update(job.id, { state: 'blocked', latest: schlankeBuehne({ ...job.stage, ...current }, job.choices),
            error: 'Ob diese Antwort ankam, ist unsicher: die Frage hat sich seither geändert. „Überarbeiten“ verwirft nur diesen Sendeversuch (deine Antwort bleibt als Entwurf) und ändert an der Bühne nichts — dort den aktuellen Stand prüfen und neu antworten.' }); return;
        }
      }
      if (!this.enabled) return;
      // Nur schreibende Abschlussversuche serialisieren; Belege dürfen parallel lesen.
      if (job.close && this.closing.size) return;
      this.update(job.id, { attempted: true, state: 'sending', error: undefined });
      if (job.close) this.closing.add(job.id);
      const r = await this.request(path + (job.raum ? '/raum' : job.close ? '/abschluss' : job.text !== undefined ? '/nachricht' : job.vorschlaege ? '/vorschlaege/antworten' : '/entscheidungen/antworten?annahme=sofort'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-guide-job-id': job.id }, signal: AbortSignal.timeout(30000),
        body: JSON.stringify(job.raum ? {
          raum: job.raum, von: 'rafael', anlass: 'setzung',
          /*
           * DER GRUND IST HIER KEIN SCHMUCK. `hof-lifecycle.ts` haelt fest: "Pausenraum
           * ist nur auf ausdrueckliche Anweisung" — der Zugschutz zwang frueher jeden
           * Berater ohne offene Frage automatisch dorthin, und Rafael hat das am
           * 17.09.2026 ausdruecklich beendet. Sein eigener Klick IST diese Anweisung;
           * der Satz hier macht im Verlauf nachpruefbar, dass es keine automatische
           * Ablage war, sondern seine.
           */
          grund: 'Rafael hat die Bühne im Guide ausdrücklich in den Pausenraum verschoben (Knopf „Auf später"). Ausdrückliche Anweisung im Sinne von hof-lifecycle.ts, keine automatische Ablage.',
        } : job.close ? { art: 'erledigt', von: 'rafael', grund: 'Rafael hat die Bühne im Guide ausdrücklich als erledigt markiert.' } : job.text !== undefined ? { id: job.id, text: job.text, von: 'rafael' } : job.vorschlaege ? { von: 'rafael', antworten: job.vorschlaege } : { von: 'rafael', antworten: job.choices }),
      });
      const d = await r.json().catch(() => null);
      if (r.ok && d?.auftragId === job.id && d.gespeichert === true) {
        if (d.status === 'done' || serverTraegtZustellung(job)) { this.accept(job); return; }
        this.update(job.id, { serverAccepted: true, state: d.status === 'blocked' ? 'blocked' : 'uncertain',
          error: d.status === 'blocked' ? d.error : job.close ? 'Abschluss läuft.' : 'Antwort gespeichert; Zustellung läuft.' });
        return;
      }
      /* Die Raum-Route antwortet mit der Buehne selbst (`fuerAnsicht`), nicht mit einem
       * Auftragsbeleg — sie geht nicht ueber den Auftrags-Worker. Bestaetigt ist sie
       * deshalb, wenn der gespeicherte Raum der gewuenschte ist; genau das prueft
       * `confirmed`, und dieselbe Pruefung traegt auch den Wiederaufnahme-Pfad oben. */
      if (job.raum ? r.ok && d && confirmed(job, d) : false) { this.accept(job); return; }
      if (job.close ? r.ok && (d?.abschluss?.art === 'erledigt' || d?.buehne?.abschluss?.art === 'erledigt') : d?.gespeichert === true || (r.ok && d && (d.buehne || d.nachricht))) {
        this.accept(job); return;
      }
      const endgueltig = r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429;
      if (endgueltig) { usage('input_state', { stage_id: job.stage.id, job_id: job.id, action: 'rejected', status: r.status }); this.abgewiesenGeprueft.set(job.id, Date.now()); }
      const wortlaut: string | undefined = typeof d?.error === 'string' ? d.error : undefined;
      const serverFehler = `Vom Server abgewiesen (HTTP ${r.status}), nicht gespeichert: ${wortlaut ?? 'ohne Begründung'}`;
      this.update(job.id, endgueltig
        ? { state: 'blocked', rejected: true, ...(istRafaelUnterschriftAbweisung(wortlaut) ? { error: ABGEWIESEN_PARTNER_TEXT, serverFehler }
          : job.close ? { error: abschlussAbgewiesenText(r.status, wortlaut), serverFehler }
          : job.raum && raumAbgewiesenText(r.status, wortlaut) ? { error: raumAbgewiesenText(r.status, wortlaut)!, serverFehler } : { error: serverFehler }) }
        : { state: 'uncertain', error: d?.error ?? `Annahme noch nicht bestätigt (HTTP ${r.status}).` });
      /* 409 auf Antworten heisst: der Stand der Fragen hat sich bewegt (umgezogen, beantwortet,
       * zurueckgezogen). Sofort nachsehen statt nach fuenf Minuten — siehe `abgewiesenLage`. */
      if (endgueltig && r.status === 409 && job.choices?.length) await this.pruefeAbgewiesen(job);
    } catch (e) {
      try { this.update(job.id, { state: 'uncertain', error: e instanceof Error ? e.message : String(e) }); }
      catch { this.fault = 'Sendestatus konnte lokal nicht aktualisiert werden. Der ursprüngliche Auftrag bleibt gespeichert.'; this.jobs = [...this.jobs]; for (const fn of this.listeners) fn(); }
    } finally { this.closing.delete(job.id); }
  }
}
